import type {
	EnergyFormValues,
	FormValues,
	RawMaterialPackagingFormValues,
	TransportFormValues,
} from '../Calculator'

export interface FieldConfig {
	type: 'select' | 'number'
	fieldName:
		| keyof TransportFormValues
		| keyof EnergyFormValues
		| keyof RawMaterialPackagingFormValues
	dependentFields?: readonly (
		| keyof TransportFormValues
		| keyof EnergyFormValues
		| keyof RawMaterialPackagingFormValues
	)[]
	options?: readonly string[]
	unit?: string
	disabled?: boolean
}

type FormConfig<FormValues> = {
	[Property in keyof FormValues]: readonly FieldConfig[]
}

export const formConfig: FormConfig<FormValues> = {
	rawmaterial: [
		{
			type: 'select',
			fieldName: 'category',
			dependentFields: ['subcategory', 'subsubcategory', 'emissionfactor'],
			options: [],
		},
		{
			type: 'select',
			fieldName: 'subcategory',
			dependentFields: ['subsubcategory', 'emissionfactor'],
			options: ['category'],
		},
		{
			type: 'select',
			fieldName: 'subsubcategory',
			dependentFields: ['emissionfactor'],
			options: ['category', 'subcategory'],
		},
		{
			type: 'select',
			fieldName: 'emissionfactor',
			dependentFields: [],
			options: ['category', 'subcategory', 'subsubcategory'],
		},
		{ type: 'number', fieldName: 'weight', unit: 'g' },
		{
			type: 'select',
			fieldName: 'transportsubcategory',
			dependentFields: ['transportsubsubcategory', 'transportemissionfactor'],
			options: ['transportcategory'],
		},
		{
			type: 'select',
			fieldName: 'transportsubsubcategory',
			dependentFields: ['transportemissionfactor'],
			options: ['transportcategory', 'transportsubcategory'],
		},
		{
			type: 'select',
			fieldName: 'transportemissionfactor',
			dependentFields: [],
			options: [
				'transportcategory',
				'transportsubcategory',
				'transportsubsubcategory',
			],
		},
		{ type: 'number', fieldName: 'distance', unit: 'km' },
	],
	packaging: [
		{
			type: 'select',
			fieldName: 'category',
			dependentFields: ['subcategory', 'subsubcategory', 'emissionfactor'],
			options: [],
		},
		{
			type: 'select',
			fieldName: 'subcategory',
			dependentFields: ['subsubcategory', 'emissionfactor'],
			options: ['category'],
		},
		{
			type: 'select',
			fieldName: 'subsubcategory',
			dependentFields: ['emissionfactor'],
			options: ['category', 'subcategory'],
		},
		{
			type: 'select',
			fieldName: 'emissionfactor',
			dependentFields: [],
			options: ['category', 'subcategory', 'subsubcategory'],
		},
		{ type: 'number', fieldName: 'weight', unit: 'g' },
		{
			type: 'select',
			fieldName: 'transportsubcategory',
			dependentFields: ['transportsubsubcategory', 'transportemissionfactor'],
			options: ['transportcategory'],
		},
		{
			type: 'select',
			fieldName: 'transportsubsubcategory',
			dependentFields: ['transportemissionfactor'],
			options: ['transportcategory', 'transportsubcategory'],
		},
		{
			type: 'select',
			fieldName: 'transportemissionfactor',
			dependentFields: [],
			options: [
				'transportcategory',
				'transportsubcategory',
				'transportsubsubcategory',
			],
		},
		{ type: 'number', fieldName: 'distance', unit: 'km' },
	],
	energy: [
		{
			type: 'select',
			fieldName: 'subcategory',
			dependentFields: ['emissionfactor'],
			options: ['category'],
		},
		{
			type: 'select',
			fieldName: 'emissionfactor',
			dependentFields: [],
			options: ['category', 'subcategory', 'subsubcategory'],
		},
		{ type: 'number', fieldName: 'amount', unit: 'kWh' },
	],
	transportBetweenSites: [
		{
			type: 'select',
			fieldName: 'subcategory',
			dependentFields: ['subsubcategory', 'emissionfactor'],
			options: ['category'],
		},
		{
			type: 'select',
			fieldName: 'subsubcategory',
			dependentFields: ['emissionfactor'],
			options: ['category', 'subcategory'],
		},
		{
			type: 'select',
			fieldName: 'emissionfactor',
			dependentFields: [],
			options: ['category', 'subcategory', 'subsubcategory'],
		},
		{ type: 'number', fieldName: 'weight', unit: 'g' },
		{ type: 'number', fieldName: 'distance', unit: 'km' },
	],
	transport: [
		{
			type: 'select',
			fieldName: 'subcategory',
			dependentFields: ['subsubcategory', 'emissionfactor'],
			options: ['category'],
		},
		{
			type: 'select',
			fieldName: 'subsubcategory',
			dependentFields: ['emissionfactor'],
			options: ['category', 'subcategory'],
		},
		{
			type: 'select',
			fieldName: 'emissionfactor',
			dependentFields: [],
			options: ['category', 'subcategory', 'subsubcategory'],
		},
		{ type: 'number', fieldName: 'weight', unit: 'g' },
		{ type: 'number', fieldName: 'distance', unit: 'km' },
	],
}

export const defaultFormValues = {
	rawmaterial: {
		category: '',
		subcategory: '',
		subsubcategory: '',
		emissionfactor: '',
		weight: '',
		transportcategory: 'Transport',
		transportsubcategory: '',
		transportsubsubcategory: '',
		transportemissionfactor: '',
		distance: '',
	},
	packaging: {
		category: '',
		subcategory: '',
		subsubcategory: '',
		emissionfactor: '',
		weight: '',
		transportcategory: 'Transport',
		transportsubcategory: '',
		transportsubsubcategory: '',
		transportemissionfactor: '',
		distance: '',
	},
	energy: {
		category: 'Energy',
		subcategory: '',
		subsubcategory: 'NaN',
		emissionfactor: '',
		amount: '',
	},
	transportBetweenSites: {
		category: 'Transport',
		subcategory: '',
		subsubcategory: '',
		emissionfactor: '',
		weight: '',
		distance: '',
	},
	transport: {
		category: 'Transport',
		subcategory: '',
		subsubcategory: '',
		emissionfactor: '',
		weight: '',
		distance: '',
	},
}
